import { produce } from 'immer'
import { useEffect, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { To, useNavigate, useSearchParams } from 'react-router-dom'

import { Button, ButtonGroup, Divider, FormGroup, H1, InputGroup, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import useAuth from '../../aws/useAuth'
import { useRootOutletContext } from '../root/Root'
import styles from './SignUp.module.scss'

export default (props: {
    redirectWhenAuthenticated: (currentSearchParams: URLSearchParams) => string,
    navigateWhenLetUserSignIn: (username: string) => To,
    navigateWhenLetUserResetPassword: (username: string) => To,
    signInLink: (currentSearchParams: URLSearchParams) => To,
}) => {
    const auth = useAuth()
    const [searchParams,] = useSearchParams()
    const { toaster } = useRootOutletContext()

    const [state, setState] = useState<{
        username: string,
        password: string,
        confirmPassword: string,
        verificationCode: string,
        step: "register" | "confirm",
        obscurityPassword: boolean,
    }>({
        username: searchParams.get("username") || "",
        password: "",
        confirmPassword: "",
        verificationCode: "",
        step: "register",
        obscurityPassword: true,
    })

    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const { executeRecaptcha } = useGoogleReCaptcha()

    // if auth state is changed, let user navigate to next page
    useEffect(() => {
        if (auth.isAuthenticated) navigate(props.redirectWhenAuthenticated(searchParams))
    }, [auth, navigate, searchParams, props])

    const executeSignUp = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const token = await executeRecaptcha?.("signUp")
        if (!token) {
            toaster?.show({ message: t('toast.recaptchaFailed.message'), icon: IconNames.Error, intent: Intent.DANGER })
            return
        }

        const result = await auth.signUp(state.username, state.password, { lang: i18n.language, recaptcha_token: token })
        if (result.success) {
            setState(produce(draft => {
                draft.step = "confirm"
            }))
            toaster?.show({ message: t('toast.verificationCodeSent.message'), icon: IconNames.Tick, intent: Intent.SUCCESS })
        } else if (result.message.includes("already exists")) {
            const token = await executeRecaptcha?.("signIn")
            if (!token) {
                toaster?.show({ message: t('toast.recaptchaFailed.message'), icon: IconNames.Error, intent: Intent.DANGER })
                return
            }
            const result = await auth.signIn(state.username, state.password, { recaptcha_token: token })
            if (result.success) {
                toaster?.show({ message: t('toast.signInSuccess.message'), icon: IconNames.Tick, intent: Intent.SUCCESS })
            } else if ((await auth.resendSignUp(state.username)).success) {
                setState(produce(draft => {
                    draft.step = "confirm"
                }))
                toaster?.show({ message: t('toast.verificationCodeSent.message'), icon: IconNames.Tick, intent: Intent.SUCCESS })
            } else {
                // bit long message, so set timeout to 10 seconds
                toaster?.show({ message: t('toast.accountAlreadyVerifiedButPasswordWrong.message'), icon: IconNames.Help, intent: Intent.WARNING, timeout: 10_000 })
                navigate(props.navigateWhenLetUserResetPassword(state.username))
            }
        } else {
            toaster?.show({ message: t('toast.invalidUsernamePassword.message'), icon: IconNames.Error, intent: Intent.DANGER })
            return
        }
    }

    const executeConfirmSignUp = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const result = await auth.confirmSignUp(state.username, state.verificationCode)
        if (result.success) {
            const token = await executeRecaptcha?.("signIn")
            if (!token) {
                toaster?.show({ message: t('toast.recaptchaFailed.message'), icon: IconNames.Error, intent: Intent.DANGER })
                return
            }
            if (state.password.length > 0) {
                const result = await auth.signIn(state.username, state.password, { recaptcha_token: token })
                if (result.success)
                    toaster?.show({ message: t('toast.signUpSuccess.message'), icon: IconNames.Tick, intent: Intent.SUCCESS })
            }
        } else if (result.message === "User cannot be confirmed. Current status is CONFIRMED") {
            navigate(props.navigateWhenLetUserSignIn(state.username))
        } else {
            toaster?.show({ message: t('toast.invalidVerificationCode.message'), icon: "error", intent: "danger" })
            return
        }
    }

    const toggleObscurityPasswordButton =
        <Button
            icon={state.obscurityPassword ? IconNames.EyeOpen : IconNames.EyeOff}
            onClick={() => setState(produce(draft => {
                draft.obscurityPassword = !draft.obscurityPassword
            }))}
            minimal
        />

    return <>
        {state.step === "register" && <>
            <h1
                className={styles.title}
            >{t('signUp.title')}</h1>
            <form onSubmit={executeSignUp}>
                <FormGroup
                    label={t('signUp.form.usernameInput.label')}
                    labelFor="username"
                    labelInfo={t('common.requiredInput.labelInfo')}>
                    <InputGroup
                        id="username"
                        leftIcon={IconNames.Envelope}
                        type="email"
                        autoComplete="email"
                        value={state.username}
                        onChange={e => setState(produce(draft => {
                            draft.username = e.target.value
                        }))}
                        required
                    />
                </FormGroup>
                <FormGroup label={t('signUp.form.passwordInput.label')}
                    labelFor="password"
                    labelInfo={t('common.requiredInput.labelInfo')}
                    subLabel={t('signUp.form.passwordInput.subLabel')}>
                    <InputGroup
                        id="password"
                        leftIcon={IconNames.Lock}
                        rightElement={toggleObscurityPasswordButton}
                        type={state.obscurityPassword ? "password" : "text"}
                        autoComplete="new-password"
                        value={state.password}
                        onChange={e => setState(produce(draft => {
                            draft.password = e.target.value
                        }))}
                        required
                        pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{12,}"
                    />
                </FormGroup>
                <FormGroup
                    label={t('signUp.form.confirmPasswordInput.label')}
                    labelFor="confirmPassword"
                    labelInfo={t('common.requiredInput.labelInfo')}>
                    <InputGroup
                        id="confirmPassword"
                        leftIcon={IconNames.Lock}
                        rightElement={toggleObscurityPasswordButton}
                        type={state.obscurityPassword ? "password" : "text"}
                        autoComplete="new-password"
                        value={state.confirmPassword}
                        onChange={e => setState(produce(draft => {
                            draft.confirmPassword = e.target.value
                        }))}
                        required
                        pattern={`^${state.password.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`}
                    />
                </FormGroup>
                <Button
                    className={styles.submitButton}
                    type="submit"
                    icon={IconNames.SendMessage}
                    intent={Intent.PRIMARY}
                    text={t('signUp.form.requestVerificationCodeButton.label')} />
            </form>

            <Divider />
            <ButtonGroup
                className={styles.footerButtonGroup}
                vertical
                minimal>
                <Button
                    text={t('signUp.navigateToSignInButton.label')}
                    rightIcon="arrow-right"
                    onClick={() => navigate(props.signInLink(searchParams))}
                />
            </ButtonGroup>
        </>}

        {state.step === "confirm" && <>
            <H1>{t('signUp.verifyTitle')}</H1>
            <p>{t('signUp.verifyDescription')}</p>
            <form onSubmit={executeConfirmSignUp}>
                <FormGroup
                    label={t('signUp.form.verificationCodeInput.label')}
                    labelFor="verificationCode"
                    labelInfo={t('common.requiredInput.labelInfo')}
                    subLabel={t('signUp.form.verificationCodeInput.subLabel')}>
                    <InputGroup
                        id="verificationCode"
                        leftIcon={IconNames.Key}
                        type="number"
                        autoComplete="off"
                        value={state.verificationCode}
                        onChange={e => setState(produce(draft => {
                            draft.verificationCode = e.target.value
                        }))}
                        required
                        pattern="[0-9]{6}"
                    />
                </FormGroup>
                <Button
                    className={styles.submitButton}
                    type="submit"
                    icon={IconNames.Endorsed}
                    intent={Intent.SUCCESS}
                    text={t('signUp.form.verifyButton.label')} />
            </form>
        </>}
    </>
}
